/**
 * watchMetaOverlay — leitura ao vivo de uma coleção pequena e SEMPRE aberta
 * (`badge_meta`/`quest_meta`, Bloco G da Admin-Alavancas —
 * `docs/systems/admin-badge-quest-meta.md` §3), usada para SOBREPOR um
 * catálogo hardcoded (badge/quest) sem round-trip síncrono no primeiro
 * render.
 *
 * Os dois consumidores (`gamificationService.ts`/`BADGE_CATALOG` e
 * `features/quests/lib/questCatalog.ts`/`QUEST_CATALOG`) são objetos
 * ESTÁTICOS de module scope, não componentes — por isso o listener também
 * nasce no module scope, em vez de um hook por catálogo (ver blueprint §3
 * para o porquê de não virar dois hooks).
 *
 * `resilientOnSnapshot`, não `onSnapshot` cru: mesmo listener morto por
 * esgotamento de retentativas que motivou aquele wrapper alcançaria este
 * overlay do mesmo jeito — a degradação aceitável aqui é "o rótulo do painel
 * não chega", nunca "a tela quebra", então a falha só é logada, nunca lançada.
 *
 * ## 🔴 Module scope ≠ "pode falar com o Firestore na hora do import"
 *
 * As duas coleções são `allow read: if isAuthed()`. Um listener criado no
 * MOMENTO DO IMPORT nasce ANTES de o Auth resolver — e em toda rota
 * deslogada (`/`, `/login`, `/livro/:slug`) ele nunca vai ter usuário. O
 * desfecho medido, com o wrapper resiliente no meio, era caro e silencioso:
 *
 *  1. `permission-denied` é erro TERMINAL → `resilientOnSnapshot` queima as
 *     4 retentativas (≈15 s, 4 attaches) em todo boot deslogado;
 *  2. esgotado o teto, ele grava `traceResume("listener-dead", …)` — o
 *     marcador de diagnóstico do BUG-197, que passa a ter ruído garantido em
 *     toda sessão anônima e deixa de discriminar o modo de falha real;
 *  3. registra um revive em `deadListeners`, que o `FirestoreResumeWatcher`
 *     re-arma em TODA retomada, para sempre;
 *  4. quem loga depois fica sem overlay até a próxima retomada — o listener
 *     morreu enquanto ninguém estava logado e não havia quem o re-assinasse.
 *
 * Por isso os dois call sites de module scope usam
 * `watchMetaOverlayWhenAuthed`: zero rede antes do Auth, assina ao entrar,
 * desassina ao sair, re-assina no próximo login. Sessão ANÔNIMA conta como
 * logada (ela satisfaz `isAuthed()` de verdade — F1.5 do Roadmap PRO), então
 * o predicado certo é `user != null`, nunca "tem conta de verdade".
 */
import { collection, onSnapshot, type DocumentData, type QuerySnapshot, type Unsubscribe } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./config";
import { resilientOnSnapshot } from "./resilientSnapshot";

export function watchMetaOverlay<T>(
  collectionName: string,
  onUpdate: (byId: Map<string, T>) => void,
): Unsubscribe {
  return resilientOnSnapshot<QuerySnapshot<DocumentData>>(
    (onNext, onError) =>
      onSnapshot(collection(db, collectionName), onNext, onError),
    (snap) => {
      const byId = new Map<string, T>();
      snap.forEach((d) => byId.set(d.id, d.data() as T));
      onUpdate(byId);
    },
    (err) => {
      // Ausência de overlay = comportamento de sempre (catálogo de código),
      // nunca tela quebrada — mesma degradação graciosa de
      // `useHomeBanners`/`app_config/runtime`.
      console.warn(`watchMetaOverlay(${collectionName}) falhou`, err);
    },
  );
}

/**
 * `watchMetaOverlay` gateado por sessão: só existe listener enquanto há
 * usuário (inclusive anônimo). Devolve o `Unsubscribe` do conjunto — parar
 * de observar o Auth E derrubar o listener corrente.
 *
 * É o que os call sites de MODULE SCOPE devem usar (ver docblock do arquivo):
 * assim o efeito colateral do import é registrar um callback local, não abrir
 * uma conexão de rede contra uma regra que vai negar.
 */
export function watchMetaOverlayWhenAuthed<T>(
  collectionName: string,
  onUpdate: (byId: Map<string, T>) => void,
): Unsubscribe {
  let inner: Unsubscribe | null = null;

  const stopAuth = onAuthStateChanged(auth, (user) => {
    if (user) {
      // `onAuthStateChanged` também dispara em TROCA de usuário sem passar
      // por `null`; manter o listener vivo é o certo (a coleção é global, não
      // por conta) e re-assinar seria um attach a mais de graça.
      if (!inner) inner = watchMetaOverlay<T>(collectionName, onUpdate);
      return;
    }
    inner?.();
    inner = null;
  });

  return () => {
    stopAuth();
    inner?.();
    inner = null;
  };
}
